import { Injectable, inject } from '@angular/core';
import { ApiService } from './api.service';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

const CSV_REQUEST_ERROR_MESSAGE = 'データ作成の応答がありませんでした。時間をおいて再度お試しください。';

@Injectable({ providedIn: 'root' })
export class StudioService {
  private api = inject(ApiService);
  private http = inject(HttpClient);

  list(scope?: number | 'all') {
    if (!scope || scope === 'all') return this.api.get('/studio');
    if (scope === 0) return this.api.get('/studio/0');
    return this.api.get(`/studio/${scope}`);
  }
  upsert(json: any) { return this.api.post('/studio', json); }
  remove(id: number) { return this.api.get(`/studio/remove/${id}`); }
  upsertStudioFee(json: any) { return this.api.post('/studio/fee', json); }
  upsertStudioPlan(json: any) { return this.api.post('/studio/plan', json); }
  removeStudioPlan(id: number) { return this.api.get(`/studio/plan/remove/${id}`); }
  upsertStudioLockerPlan(json: any) { return this.api.post('/studio/locker/plan', json); }
  getMember(id: number) { return this.api.get(`/studio/${id}/member`); }
  getMemberDetail(id: number) { return this.api.get(`/studio/${id}/member/detail`); }
  getMemberCount(id: number | string) { return this.api.get(`/studio/${id}/member/count`); }
  getTransaction(id: number, year: number, month: number) {
    return this.api.get(`/studio/${id}/transaction/${year}/${month}`);
  }
  getInvoice(id: number, year: number, month: number) {
    return this.api.get(`/studio/${id}/invoice/${year}/${month}`);
  }
  getBankTransferUrl(trusteeCode: string, year: number, month: number) {
    return this.api.get(`/bt/${trusteeCode}/${year}/${month}`);
  }
  getBalance(id: number, year: number, month: number) {
    return this.api.get(`/studio/${id}/balance/${year}/${month}`);
  }
  async getLocalNumberList() {
    // 旧版 StudioFactory.getLocalNumberList (admin/js/factories/studio.js:313-315) と同じく親地域を除外する
    const result = await this.api.get<any>('/studio/local');
    return Array.isArray(result) ? result.filter((v: any) => v.isParent != 1) : result;
  }
  getProduct(id: number | string) { return this.api.get(`/studio/${id}/product`); }
  getLockerNumber(id: number) { return this.api.get(`/studio/${id}/lockerNumber`); }
  getStudioPlan(id: number) { return this.api.get(`/studio/plan/${id}`); }
  getStaff(id: number) { return this.api.get(`/studio/${id}/staff`); }
  getKeyNumber(id: number) { return this.api.get(`/studio/${id}/keyNumber`); }
  getNotification(id: number) { return this.api.get(`/studio/${id}/notification`); }
  availableList() { return this.api.get('/studio/available'); }
  getInout(id: number | string, startPoint: string, endPoint: string) {
    return this.api.get(`/studio/${id}/inout/${startPoint}/${endPoint}`);
  }
  addInout(id: number, json: any, isFr = false) {
    return this.api.post(`/studio/${id}/inout${isFr ? '/fr' : ''}`, json);
  }
  getAggregationMembership(id: number, year: number) {
    return this.api.get(`/studio/${id}/aggregation/${year}`);
  }
  getAggregationMemberRatio(id: number, year: number) {
    return this.api.get(`/studio/${id}/aggregation`);
  }
  getPlanType() { return this.api.get('/studioPlanType'); }
  getExportData(studioId: number, issueMonth: string, btMethod: string) {
    return this.api.get(`/invoice/export/${studioId}/${issueMonth}/${btMethod}`);
  }
  getPlanForMoveStudio(studioId: number) { return this.api.get(`/studio/${studioId}/move_studio`); }
  getPromotion(studioId: number, targetMonth: string) {
    return this.api.get(`/studio/${studioId}/marketing/promotion/${targetMonth}`);
  }
  getPT(studioId: number, targetMonth: string) {
    return this.api.get(`/studio/${studioId}/marketing/personal_training_fee/${targetMonth}`);
  }
  getVisitor(studioId: number, targetMonth: string) {
    return this.api.get(`/studio/${studioId}/marketing/visitor/${targetMonth}`);
  }
  getNumberOfMembers(studioId: number, targetMonth: string) {
    return this.api.get(`/studio/${studioId}/marketing/number_of_members/${targetMonth}`);
  }
  getIncrease(studioId: number, targetMonth: string) {
    return this.api.get(`/studio/${studioId}/marketing/increase/${targetMonth}`);
  }
  getDisable(studioId: number, targetMonth: string) {
    return this.api.get(`/studio/${studioId}/marketing/disable/${targetMonth}`);
  }
  getEnable(studioId: number, targetMonth: string) {
    return this.api.get(`/studio/${studioId}/marketing/enable/${targetMonth}`);
  }
  getInsuranceProduct(id: number) { return this.api.get(`/studio/${id}/insurance/product`); }
  getInsuranceLog(id: number, type: string) {
    return this.api.get(`/studio/${id}/insurance/type/${type}`);
  }
  getInsurance(id: number, month: string) {
    return this.api.get(`/studio/${id}/insurance/${month}`);
  }
  getMemberCsv(id: number | string) { return this.api.get(`/studio/${id}/member/csv/`); }
  getNotificationCsv(id: number | string) { return this.api.get(`/studio/${id}/notification/csv/`); }
  getInoutCsv(id: number | string) { return this.api.get(`/studio/${id}/inout/csv/`); }

  async createMemberCsv(id: number | string): Promise<string | null> {
    return this.createCsv(`/studio/${id}/member/csv/`);
  }

  async createNotificationCsv(id: number | string): Promise<string | null> {
    return this.createCsv(`/studio/${id}/notification/csv/`);
  }

  async createInoutCsv(id: number | string): Promise<string | null> {
    return this.createCsv(`/studio/${id}/inout/csv/`);
  }

  private async createCsv(path: string): Promise<string | null> {
    try {
      const res = await firstValueFrom(this.api.getRaw<any>(path));
      return res?.error ?? res?.result?.error ?? null;
    } catch (err) {
      console.error('CSV生成リクエスト失敗', err);
      return CSV_REQUEST_ERROR_MESSAGE;
    }
  }

  async downloadBTExportFile(btId: number, filePath: string): Promise<void> {
    const fileName = filePath.split('/').pop() ?? 'export.csv';
    const blob = await firstValueFrom(
      this.http.get(`${environment.apiBaseUrl}/bt/${btId}`, { responseType: 'blob' })
    );
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  async uploadBTImportFile(btId: number, fileData: FormData): Promise<any> {
    return this.api.post(`/bt/${btId}`, fileData);
  }
}
